import React, { useState, useRef, useEffect } from 'react';
import { Card, CardBody, Progress, Tooltip} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { MatchBreakdownButtons } from './MatchBreakdownButtons';
import { StopGatesSection, StopGateItem } from './StopGatesSection';
import { useScoreThresholds, type ScoreThresholds } from './claimScoreRendererShared';

export interface ScoreCalculationItem {
    name: string;
    score: number;
}

export interface ScoreCardProps {
    displayScore: string | null;
    scoreValue: number;
    scoreColor: 'success' | 'warning' | 'danger';
    matchBreakdown: {
        matches: number;
        partial: number;
        mismatches: number;
    };
    activeFilter: 'matches' | 'partial' | 'mismatches' | null;
    onFilterClick: (filterType: 'matches' | 'partial' | 'mismatches') => void;
    feedbackComponent?: React.ReactNode;
    scoreColorClass?: string;
    cardHeight?: string;
    /** When provided, info button flips card to show score calculation breakdown (ClaimScoreGeneratorRenderer only) */
    scoreCalculationItems?: ScoreCalculationItem[];
    showMatchBreakdown?: boolean;
    stopGates?: StopGateItem[];
    activeStopGate?: string | null;
    onStopGateClick?: (key: string) => void;
}

const cardStyle: React.CSSProperties = {
    height: 'auto',
    flexShrink: 0,
    borderRadius: '10px',
    border: '1px solid rgba(0, 0, 0, 0.10)',
    background: 'var(--cty-bg-surface)'
};

const getScoreTextColor = (score: number, thresholds: ScoreThresholds): string => {
    const n = Math.round(score);
    if (n >= thresholds.greenMin) return 'text-green-600';
    if (n >= thresholds.yellowMin) return 'text-amber-600';
    return 'text-red-600';
};

const getScoreBgColor = (score: number, thresholds: ScoreThresholds): string => {
    const n = Math.round(score);
    if (n >= thresholds.greenMin) return 'bg-green-100';
    if (n >= thresholds.yellowMin) return 'bg-amber-100';
    return 'bg-red-100';
};

const getScoreIndicatorBg = (scoreColor: 'success' | 'warning' | 'danger'): string => {
    if (scoreColor === 'danger') return '!bg-red-600';
    if (scoreColor === 'warning') return '!bg-yellow-600';
    return '!bg-green-600';
};

export const ScoreCard: React.FC<ScoreCardProps> = ({
    displayScore,
    scoreValue,
    scoreColor,
    matchBreakdown,
    activeFilter,
    onFilterClick,
    feedbackComponent,
    scoreColorClass,
    cardHeight = 'auto',
    scoreCalculationItems,
    showMatchBreakdown = true,
    stopGates = [],
    activeStopGate = null,
    onStopGateClick
}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [backHeight, setBackHeight] = useState<number | null>(null);
    const backRef = useRef<HTMLDivElement>(null);
    // Agent-config driven thresholds for the per-item score badges (falls back to 85/60).
    const scoreThresholds = useScoreThresholds();
    const canFlip = scoreCalculationItems != null && scoreCalculationItems.length > 0;

    useEffect(() => {
        if (!canFlip || !isFlipped) {
            setBackHeight(null);
            return;
        }
        const measure = () => {
            if (backRef.current) {
                setBackHeight(backRef.current.offsetHeight);
            }
        };
        const id = requestAnimationFrame(() => requestAnimationFrame(measure));
        return () => cancelAnimationFrame(id);
    }, [canFlip, isFlipped]);

    if (!displayScore) return null;

    let scoreTextColor: string;
    if (scoreColorClass) {
        scoreTextColor = scoreColorClass;
    } else if (scoreColor === 'danger') {
        scoreTextColor = 'text-red-600 dark:text-red-400';
    } else if (scoreColor === 'warning') {
        scoreTextColor = 'text-yellow-600 dark:text-yellow-400';
    } else {
        scoreTextColor = 'text-green-600 dark:text-green-400';
    }

    const frontFace = (
        <Card className="mb-2 mt-2 w-full" style={cardStyle}>
            <CardBody className="p-3 py-3">
                <div className="relative">
                    {feedbackComponent && (
                        <div className="absolute top-0 right-0 pl-3 pb-3 z-10">
                            {feedbackComponent}
                        </div>
                    )}
                    <div className={`flex items-start justify-between ${feedbackComponent ? 'pr-10' : ''}`}>
                        <div>
                            <div className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 ${scoreTextColor}`}>
                                {displayScore}
                            </div>
                        </div>
                    </div>

                    <div className="mb-2">
                        <div className="flex items-center gap-1">
                            <div className="renderer-text-secondary mb-1">Score Calculation</div>
                            {canFlip && (
                                <Tooltip content="open score calculation" placement="top" showArrow>
                                <button
                                    type="button"
                                    onClick={() => setIsFlipped(true)}
                                    className="p-1 rounded-full hover:bg-default-100"
                                    aria-label="Show score calculation"
                                >
                                    <Icon icon="mdi:information-outline" className="w-4 h-4 text-default-400 mb-1" />
                                </button>
                                </Tooltip>
                            )}
                        </div>
                        <Progress
                            value={scoreValue}
                            classNames={{
                                track: "h-[7px]",
                                indicator: `${getScoreIndicatorBg(scoreColor)} h-[7px]`
                            }}
                            showValueLabel={false}
                        />
                    </div>

                    {showMatchBreakdown && (
                        <MatchBreakdownButtons
                        matches={matchBreakdown.matches}
                        partial={matchBreakdown.partial}
                        mismatches={matchBreakdown.mismatches}
                        activeFilter={activeFilter}
                        onFilterClick={onFilterClick}
                    />
                    )}

                    {stopGates.length > 0 && (
                        <StopGatesSection
                            items={stopGates}
                            activeKey={activeStopGate}
                            onItemClick={(key) => onStopGateClick?.(key)}
                        />
                    )}
                </div>
            </CardBody>
        </Card>
    );

    const backFace = (
        <Card className="mb-2 mt-2 w-full" style={cardStyle}>
            <CardBody className="p-3 py-3">
                <div className="relative">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-1">
                            <span className="font-semibold text-foreground">Score Calculation</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsFlipped(false)}
                            className="p-1 rounded-full hover:bg-default-100"
                            aria-label="Close"
                        >
                            <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
                        </button>
                    </div>

                    <div className="space-y-2 mb-3">
                        {scoreCalculationItems?.map((item) => (
                            <div key={item.name} className="flex items-center justify-between">
                                <span className="text-sm font-medium text-foreground">{item.name}</span>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getScoreTextColor(item.score, scoreThresholds)} ${getScoreBgColor(item.score, scoreThresholds)}`}>
                                    {Math.round(item.score)}%
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="border-t border-default-200 pt-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-default-500 font-medium">Rate this analysis:</span>
                            <div className="flex items-center gap-2">{feedbackComponent}</div>
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );

    if (!canFlip) {
        return frontFace;
    }

    return (
        <div
            className="mb-2 mt-2"
            style={{
                perspective: '1000px',
                ...(isFlipped && backHeight != null ? { height: backHeight } : {})
            }}
        >
            <div
                className="relative w-full"
                style={{
                    transformStyle: 'preserve-3d',
                    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    transition: 'transform 0.5s ease',
                    ...(isFlipped && backHeight != null ? { height: backHeight } : {})
                }}
            >
                <div style={{ backfaceVisibility: 'hidden' }}>{frontFace}</div>
                <div
                    ref={backRef}
                    className="absolute top-0 left-0 right-0 w-full"
                    style={{
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                    }}
                >
                    {backFace}
                </div>
            </div>
        </div>
    );
};